import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tag, message, Modal, Switch, Tooltip, Tabs } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import Layout from "../../components/Layout";
import { getCommissionTypes, deleteCommissionType, toggleCommissionTypeStatus } from "../../services/commission/commissionServices";
import CommissionTypeModal from "./modals/CommissionTypeModal";
import GlassCard from "../../components/premium/GlassCard";
import CommissionCategories from "./CommissionCategories";

const { TabPane } = Tabs;

const { confirm } = Modal;

const CommissionTypeList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await getCommissionTypes();
      if (res?.success) {
        setData(res.data);
      } else {
        message.error(res?.message || "Failed to fetch commission types");
      }
    } catch (error) {
      message.error("Error fetching commission types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleToggleStatus = async (record) => {
    try {
      const res = await toggleCommissionTypeStatus(record.id);
      if (res?.success) {
        message.success(`Status updated for ${record.commissionName}`);
        fetchTypes();
      } else {
        message.error(res?.message || "Failed to update status");
      }
    } catch (error) {
      message.error("Error updating status");
    }
  };

  const handleDelete = (record) => {
    confirm({
      title: "Are you sure you want to delete this commission type?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const res = await deleteCommissionType(record.id);
          if (res?.success) {
            message.success("Commission type deleted successfully");
            fetchTypes();
          } else {
            message.error(res?.message || "Failed to delete");
          }
        } catch (error) {
          message.error("Error deleting commission type");
        }
      },
    });
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "commissionCode",
      key: "commissionCode",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Name",
      dataIndex: "commissionName",
      key: "commissionName",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Category",
      dataIndex: "commissionCategory",
      key: "commissionCategory",
      render: (category) => category?.displayName || "—",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch 
          checked={isActive} 
          onChange={() => handleToggleStatus(record)} 
          size="small"
        />
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined style={{ color: "#1890ff" }} />} 
              onClick={() => {
                setSelectedRecord(record);
                setIsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="text" 
              icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />} 
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: "24px" }}>
        <Tabs defaultActiveKey="types" className="premium-tabs">
          <TabPane tab="Commission Types" key="types">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
              <div>
                <h2 style={{ margin: 0 }}>Commission Types</h2>
                <p style={{ color: "var(--text-muted)" }}>Manage broad categories of commissions.</p>
              </div>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
                style={{ borderRadius: "8px", height: "40px" }}
              >
                Create Commission Type
              </Button>
            </div>

            <GlassCard>
              <Table 
                columns={columns} 
                dataSource={data} 
                loading={loading} 
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </GlassCard>

            <CommissionTypeModal 
              visible={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              onSuccess={() => {
                setIsModalVisible(false);
                fetchTypes();
              }}
              initialValues={selectedRecord}
            />
          </TabPane>
          <TabPane tab="Categories" key="categories">
            <CommissionCategories />
          </TabPane>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CommissionTypeList;
